import React, { useState, useRef } from 'react';
import { MinusIcon } from '../icons/minus.icon';
import { PlusIcon } from '../icons/plus.icon';
import { EyeIcon } from '../icons/eye.icon';
import { EyeOffIcon } from '../icons/eyeOff.icon';
import ValidationIndicator from '../others/validationIndicator.component';

interface InputTextProps {
  className?: string;
  label?: string;
  value?: string | number;
  placeholder? : string;
  type?: 'text' | 'number' | 'password';
  min?: number;
  max?: number;
  minCharacter?: number;
  maxCharacter?: number;
  onChange?: ( value: string ) => void;
  disable?: boolean;
  isRequired?: boolean;
  labelPosition?: 'top' | 'left';
  icon?: React.ReactNode;
}

const InputText: React.FC<InputTextProps> = ( {
  className,
  label,
  isRequired = false,
  value = '',
  placeholder = '',
  type = 'text',
  disable = false,
  min,
  max,
  minCharacter,
  icon,
  maxCharacter,
  onChange,
  labelPosition = 'left',
} ) => {
  const [ isValid, setIsValid ] = useState<boolean>( false );
  const [ showPassword, setShowPassword ] = useState( false );
  const [ nbrChara, setNbrChara ] = useState<number>( value.toString().length );
  const inputRef = useRef<HTMLInputElement>( null );

  const handleChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    let inputValue = e.target.value;

    if ( type === 'number' ) {
      inputValue = inputValue.replace( /[^\d]/g, '' );
      if ( min !== undefined && Number( inputValue ) < min ) {return;}
      if ( max !== undefined && Number( inputValue ) > max ) {return;}
    }

    if ( maxCharacter ) {
      if ( inputValue.toString().length > maxCharacter ) {
        inputValue = inputValue.toString().slice( 0, maxCharacter );
      }
    }
    setNbrChara( inputValue.toString().length );

    onChange?.( inputValue );

    const length = String( inputValue ).length;
    if ( isRequired && ( !inputValue || inputValue === '' ) ) {
      setIsValid( false );
    } else if (
      ( minCharacter && length < minCharacter ) ||
      ( maxCharacter && length > maxCharacter )
    ) {
      setIsValid( false );
    } else {
      setIsValid( true );
    }
  };

  const increment = () => {
    if ( disable || type !== 'number' ) {return;}
    const current = Number( value || 0 );
    const next = max !== undefined ? Math.min( current + 1, max ) : current + 1;
    onChange?.( String( next ) );
  };

  const decrement = () => {
    if ( disable || type !== 'number' ) {return;}
    const current = Number( value || 0 );
    const next = min !== undefined ? Math.max( current - 1, min ) : current - 1;
    onChange?.( String( next ) );
  };

  const handleKeyDown = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( type === 'number' ) {
      const allowedKeys = [
        'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete',
      ];

      if ( !/^\d$/.test( e.key ) && !allowedKeys.includes( e.key ) ) {
        e.preventDefault();
      }

      if ( e.key === 'ArrowUp' ) {
        setIsValid( true );
        e.preventDefault();
        increment();
      } else if ( e.key === 'ArrowDown' ) {
        setIsValid( true );
        e.preventDefault();
        decrement();
      }
    }
  };

  return (
    <div className={`w-full rounded-md ${className} ${labelPosition === 'left' ? 'flex' : ''}`}>
      {label && (
        <label className={`text-gray-600 w-3/12 mr-4 ${labelPosition === 'left' ? 'flex items-center' : ''}`}>
          {label}
        </label>
      )}
      <div className={`relative w-full ${labelPosition === 'top' && 'mt-2'} flex items-center`}>
        <ValidationIndicator isRequired={isRequired} isValid={isValid} />
        {icon && <div className="absolute left-4 text-gray-500">{icon}</div>}
        <input
          ref={inputRef}
          disabled={disable}
          value={value}
          type={type === 'password' && showPassword ? 'text' : type}
          placeholder={placeholder}
          min={type === 'number' ? min : undefined}
          max={type === 'number' ? max : undefined}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={`${icon ? 'pl-16' : 'pl-2'} p-2 w-full shadow-md outline-none rounded-md border-[1px] ${
            disable ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'
          } border-black hover:border-black focus:border-black focus:ring-1 focus:ring-black`}
          onPaste={e => {
            if ( type === 'number' ) {
              const pasted = e.clipboardData.getData( 'text' );
              if ( !/^\d+$/.test( pasted ) ) {
                e.preventDefault();
              }
            }
          }}
        />

        {type === 'number' && (
          <div className="absolute right-0 top-0 pr-2 h-full flex items-center gap-2">
            <MinusIcon
              size={7}
              className={`cursor-pointer hover:bg-gray-200 trans-fast rounded-md p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`}
              onClick={decrement}
            />
            <PlusIcon
              size={7}
              className={`cursor-pointer hover:bg-gray-200 trans-fast rounded-md p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`}
              onClick={increment}
            />
          </div>
        )}

        {type === 'password' && (
          <div className="absolute right-0 top-0 pr-2 h-full flex items-center gap-2">
            {showPassword ? (
              <EyeIcon
                size={7}
                onClick={() => {return setShowPassword( prev => {return !prev;} );}}
                className={`cursor-pointer hover:bg-gray-300 trans-fast rounded-full p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`}
              />
            ) : (
              <EyeOffIcon
                size={7}
                onClick={() => {return setShowPassword( prev => {return !prev;} );}}
                className={`cursor-pointer hover:bg-gray-300 trans-fast rounded-full p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`}
              />
            )}
          </div>
        )}
        {minCharacter || maxCharacter && (
          <div className="flex text-sm absolute top-full w-full justify-end items-center">
            <div className='flex-center gap-2 px-4 p-1'>
              {minCharacter && (
                <p>{minCharacter} /</p>
              )}
              <p>{nbrChara}</p>
              {maxCharacter && (
                <p>/ {maxCharacter}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputText;
